import React, { useState } from 'react';
import { Form, Input, Button, Alert, Typography, Card, Space, Flex, Tag, Grid } from 'antd';
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone, CrownOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Footer from '../Layout/Footer';

const { Title, Text, Link } = Typography;
const { useBreakpoint } = Grid;

const AdminLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();

  const onFinish = async (values) => {
    setIsLoading(true);
    setError('');

    try {
      const success = await login(values, true); // true para admin
      if (success) {
        // AdminProtectedRoute bifurca según el perfil: un fiscal-admin acaba en
        // /fiscal/cartera. Se decide ahí y no aquí para que también aplique al
        // recargar o al navegar directo a /admin/*.
        navigate('/admin/dashboard');
      } else {
        setError('Credenciales de administrador inválidas');
      }
    } catch (err) {
      setError('Error de conexión. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1E1E2D 0%, #4B418F 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        padding: '20px',
      }}
    >
      {/* Efectos de fondo */}
      <div
        style={{
          position: 'absolute',
          top: '-10%',
          left: '-5%',
          width: screens.md ? '300px' : '150px',
          height: screens.md ? '300px' : '150px',
          background: 'radial-gradient(circle, rgba(110, 98, 196, 0.4) 0%, rgba(0,0,0,0) 70%)',
          borderRadius: '50%',
          filter: 'blur(40px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-10%',
          right: '-5%',
          width: screens.md ? '400px' : '200px',
          height: screens.md ? '400px' : '200px',
          background: 'radial-gradient(circle, rgba(250, 173, 20, 0.2) 0%, rgba(0,0,0,0) 70%)',
          borderRadius: '50%',
          filter: 'blur(50px)',
        }}
      />

      <Card
        style={{
          width: '100%',
          maxWidth: '420px',
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          zIndex: 1,
          padding: '20px',
        }}
      >
        <Flex vertical align="center" gap="large">
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '72px',
                height: '72px',
                background: 'linear-gradient(135deg, #FAAD14 0%, #D48806 100%)',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                boxShadow: '0 10px 20px rgba(250, 173, 20, 0.3)',
                transform: 'rotate(-5deg)',
              }}
            >
              <CrownOutlined style={{ fontSize: '36px', color: 'white', transform: 'rotate(5deg)' }} />
            </div>
            <Title level={2} style={{ margin: '0 0 8px', color: '#1E1E2D', fontWeight: 800 }}>
              Panel Administrativo
            </Title>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              Acceso exclusivo para gestión
            </Text>
          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              style={{ width: '100%', borderRadius: '12px' }}
            />
          )}

          <Form
            form={form}
            name="adminLogin"
            onFinish={onFinish}
            layout="vertical"
            style={{ width: '100%' }}
            size="large"
          >
            <Form.Item
              label={<Text strong style={{ color: '#5A5D7A' }}>Credencial de Administrador</Text>}
              name="email"
              rules={[{ required: true, message: 'Ingresa tu email administrativo' }]}
            >
              <Input
                prefix={<UserOutlined style={{ color: '#6E62C4' }} />}
                placeholder="admin@mifactura.com"
                style={{
                  backgroundColor: '#F7F8FC',
                  border: '1px solid #E3E5F1',
                  borderRadius: '12px',
                  height: '48px',
                }}
              />
            </Form.Item>

            <Form.Item
              label={<Text strong style={{ color: '#5A5D7A' }}>Código de Acceso</Text>}
              name="password"
              rules={[{ required: true, message: 'Ingresa tu contraseña' }]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#6E62C4' }} />}
                placeholder="••••••••••••"
                style={{
                  backgroundColor: '#F7F8FC',
                  border: '1px solid #E3E5F1',
                  borderRadius: '12px',
                  height: '48px',
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 12, marginTop: 8 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
                size="large"
                style={{
                  height: '52px',
                  borderRadius: '12px',
                  background: 'linear-gradient(90deg, #1E1E2D 0%, #4B418F 100%)',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: 700,
                  boxShadow: '0 8px 16px rgba(75, 65, 143, 0.2)',
                }}
              >
                {isLoading ? 'Verificando...' : 'INICIAR SESIÓN'}
              </Button>
            </Form.Item>
          </Form>

          <Flex align="center" gap="small" style={{ opacity: 0.8 }}>
            <ThunderboltOutlined style={{ color: '#FAAD14' }} />
            <Text type="secondary" style={{ fontSize: '13px' }}>
              Sistema Seguro de Gestión
            </Text>
          </Flex>

          <Link
            onClick={() => navigate('/login')}
            style={{
              color: '#6E62C4',
              fontWeight: 600,
            }}
          >
            ← Volver al acceso de usuarios
          </Link>
        </Flex>
      </Card>
      
      <div style={{ position: 'absolute', bottom: 24, color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
        © 2026 Mi Factura Platform. Admin Console v2.0
      </div>
    </div>
  );
};

export default AdminLogin;